import React from 'react';
import { useDispatch } from 'react-redux';
import { setEnableClosedSidebar } from '../../../../../store/actions/themeActions/ThemeOptions';
import Loader from 'react-loaders';
import BlockUi from '@availity/block-ui';
import useMedia from '../hooks/media';
import cx from 'classnames';
import logoInverse from '../../../../../assets/utils/images/logo-inverse.png';
import logo from '../../../../../assets/utils/images/logo.png';
import ProBadge from '../badges/ProBadge';
import { HamburgerButton } from '../buttons/HeaderIconButton';
import { useAppSelector } from '../../../../../store/hooks';

interface CorporateLogoProps {
    variant?: 'dynamic' | 'static';
    inverse?: boolean;
    showHamburger?: boolean;
    showProBadge?: boolean;
    className?: string;
    containerClassName?: string;
    align?: 'left' | 'center' | 'right' | 'end';
    onHamburgerClick?: () => void;
}

const CorporateLogo: React.FC<CorporateLogoProps> = ({
    variant = 'dynamic',
    inverse = false,
    showHamburger = false,
    showProBadge = false,
    className,
    containerClassName,
    align = 'left',
    onHamburgerClick,
}) => {
    const { media, isFetchingLogo } = useMedia('user_logo', variant === 'static');
    const enableClosedSidebar = useAppSelector((state: any) => state.ThemeOptions.enableClosedSidebar);
    const dispatch = useDispatch();

    const handleHamburgerClick = () => {
        if (onHamburgerClick) {
            onHamburgerClick();
        } else {
            dispatch(setEnableClosedSidebar(!enableClosedSidebar));
        }
    };

    const getAlignClass = () => {
        switch (align) {
            case 'center':
                return 'justify-content-center';
            case 'right':
            case 'end':
                return 'justify-content-end';
            default:
                return 'justify-content-start';
        }
    };

    const getLogoClass = () => {
        return inverse ? 'app-logo-inverse' : 'logo-src';
    };

    const getBackgroundImage = () => {
        if (variant === 'dynamic' && media) {
            return `url('${media}')`;
        }
        if (variant === 'static') {
            return inverse ? `url('${logo}')` : `url('${logoInverse}')`;
        }
        return undefined;
    };

    const logoElement = (
        <div
            className={cx(getLogoClass(), className)}
            style={{
                backgroundImage: getBackgroundImage(),
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
            }}
        />
    );

    const containerClass = variant === 'static' ? 'd-flex' : 'app-header__logo';

    const logoWithBadge = (
        <div className="d-flex align-items-center">
            {variant === 'dynamic' ? (
                <BlockUi
                    tag="div"
                    blocking={isFetchingLogo}
                    className="block-overlay-light pt-2"
                    loader={<Loader active type="line-scale-party" />}
                >
                    {logoElement}
                </BlockUi>
            ) : (
                logoElement
            )}
            {showProBadge && !enableClosedSidebar && <ProBadge className="ml-1" />}
        </div>
    );

    return (
        <div className={cx(containerClass, getAlignClass(), containerClassName)}>
            {logoWithBadge}
            {showHamburger && (
                <div className="header__pane ml-auto">
                    <HamburgerButton onClick={handleHamburgerClick} />
                </div>
            )}
        </div>
    );
};

export default CorporateLogo;
